import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FloatingTextProps } from "./Hotspot.d";
import RichText from '@/utils/RichText'
import useResponsive from '@/hooks/useResponsive'

const FloatingText = ({
  hotspot,
  getPosition,
  getLaptopBottom,
  viewportSize,
  indicatorSize,
}: FloatingTextProps) => {
  const textBoxRef = useRef<HTMLDivElement>(null);
  const [textBoxSize, setTextBoxSize] = useState({ width: 300, height: 0 });
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const { isMobile } = useResponsive()

  useEffect(() => {
    // For mobile, we need the laptop position
    if (isMobile) {
      // Trigger render with a dummy position so we can get laptop position
      setPosition({ x: 0, y: 0 });
    } else {
      // Desktop positioning logic
      const pos = getPosition(hotspot);
      if (pos) {
        // Set initial position with some basic calculations for desktop
        const initialPos = {
          x: hotspot.bubble_position === "left" ? pos.x - 330 : pos.x + 30,
          y: pos.y - 50,
        };
        setPosition(initialPos);

        const timer = setTimeout(() => {
          if (textBoxRef.current) {
            const newSize = {
              width: textBoxRef.current.offsetWidth,
              height: textBoxRef.current.offsetHeight,
            };
            setTextBoxSize(newSize);

            // Recalculate for desktop view
            const hotspotPos = getPosition(hotspot);
            if (hotspotPos) {
              const optimizedPos = calculateOptimalPosition(
                hotspotPos,
                newSize,
                viewportSize,
                hotspot.bubble_position
              );
              setPosition(optimizedPos);
            }
          }
        }, 50);

        return () => clearTimeout(timer);
      }
    }
  }, [hotspot, getPosition, viewportSize, isMobile]);

  // Update position if textBoxSize changes (desktop only)
  useEffect(() => {
    if (!isMobile && textBoxRef.current && position) {
      const newPos = calculateOptimalPosition(
        position,
        textBoxSize,
        viewportSize,
        hotspot.bubble_position
      );
      setPosition(newPos);
    }
  }, [textBoxSize, isMobile]);

  if (!position) return null;

  // @INFO: Calculate optimal position for the tooltip that stays closer to hotspot
  function calculateOptimalPosition(
    hotspotPos: { x: number; y: number },
    textSize: { width: number; height: number },
    viewport: { width: number; height: number },
    preferredDirection: "left" | "right"
  ) {
    const margin = 20; // Minimum margin from viewport edges
    const tooltipSpacing = 30; // Shorter distance from hotspot (pixels)

    // Initial position based on preferred direction
    let posX =
      preferredDirection === "left"
        ? (hotspotPos.x - textSize.width) * tooltipSpacing
        : hotspotPos.x + tooltipSpacing + 50;

    // Vertical alignment - center with hotspot by default
    let posY = hotspotPos.y - textSize.height / 2;

    // Adjust if tooltip would go outside viewport
    // Right edge collision
    if (posX + textSize.width + margin > viewport.width) {
      posX = hotspotPos.x - textSize.width - tooltipSpacing;
    }

    // Left edge collision
    if (posX < margin) {
      posX = margin;
    }

    // Top edge collision
    if (posY < margin) {
      posY = margin;
    }

    // Bottom edge collision
    if (posY + textSize.height + margin > viewport.height) {
      posY = viewport.height - textSize.height - margin;
    }

    return { x: posX, y: posY };
  }

  // @INFO: Determine if tooltip is on left or right side of hotspot (for desktop)
  const isLeftSide = !isMobile && position.x < (getPosition(hotspot)?.x || 0);

  // Mobile layout - positioned centered below the laptop
  if (isMobile) {
    const laptopBottom = getLaptopBottom();

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={hotspot._uid}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed z-50 pointer-events-auto w-full flex items-center justify-center rounded-full"
          style={{
            top: laptopBottom?.y || "80vh",
          }}
          ref={textBoxRef}
          data-testid="floating-text-container"
        >
          <motion.div
            className="relative max-w-[350px]"
            data-testid="floating-text-shape-mobile"
            initial={{ scale: 0.1 }}
            animate={{ scale: 1 }}
          >
            <motion.div
              className="absolute border-2 border-[#01a077] bg-brightgreen rounded-full w-full h-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1.1, opacity: 1 }}
              transition={{ duration: 0.2, delay: 0.2 }}
            />
            <div className="relative text-white p-5 rounded-full">
              <RichText>{hotspot.content}</RichText>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Desktop layout with accurate positioning
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={hotspot._uid}
        data-testid="floating-text-container-desktop"
        className="fixed z-50 pointer-events-auto "
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        style={{
          left: `${position.x}px`,
          top: `${(position.y + indicatorSize)}px`,
          width: "300px",
        }}
        ref={textBoxRef}
      >
        <div className="relative">
          <motion.div
            className="absolute rounded-full w-full h-full"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          />
          <motion.div
            className="relative border-4 border-[#01a077] bg-brightgreen text-white p-10 rounded-full text-left aspect-square flex items-center justify-center"
            data-testid="floating-text-shape-desktop"
            initial={{ scale: 1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <RichText>{hotspot.content}</RichText>
          </motion.div>

          {/* @INFO: Show connecting lines on desktop only */}
          {position && getPosition(hotspot) && (
            <>
              {/* Highlighted circle around active hotspot */}
              <motion.div
                data-testid="clicked-indicator"
                className="fixed pointer-events-none z-40"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                style={{
                  left: `${getPosition(hotspot)!.x}px`,
                  top: `${getPosition(hotspot)!.y}px`,
                  width: `${indicatorSize}px`,
                  height: `${indicatorSize}px`,
                }}
              />

              {/* Connecting line (desktop only) */}
              {!isMobile && (
                <motion.div
                  data-testid="connecting-line"
                  className="absolute h-8 rounded-full antialiased"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  style={{
                    position: "fixed",
                    top: `${getPosition(hotspot)!.y - 15}px`,
                    transformOrigin: isLeftSide ? "right" : "left",
                    ...(isLeftSide
                      ? {
                        left: `${position.x + textBoxSize.width}px`,
                        width: `${Math.abs(
                          position.x +
                          textBoxSize.width -
                          getPosition(hotspot)!.x
                        ) - 15
                          }px`,
                      }
                      : {
                        left: `${getPosition(hotspot)!.x + 15}px`,
                        width: `${Math.abs(position.x - getPosition(hotspot)!.x) - 15
                          }px`,
                      }),
                    backgroundImage: `radial-gradient(circle, #00D2A0 4px, transparent 4px)`,
                    backgroundSize: "20px",
                    backgroundRepeat: "repeat-x",
                    backgroundPosition: "center",
                  }}
                />
              )}
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingText;
